import './BatteryList.scss'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Container, Form, ListGroup, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { Batteries } from '../../api/Batteries';
import { useNavigate } from 'react-router-dom';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import IconButton from '../../components/IconButton';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Battery, BatteryUploadDTO } from '../../models/Battery';
import { Organization } from '../../models/Organization';
import { Organizations } from '../../api/Organizations';

export default function BatteriesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [batteries, setBatteries] = useState<Battery[] | null>();
  const [organizations, setOrganizations] = useState<Organization[]>();

  const [newBattery, setNewBattery] = useState<Battery>(new Battery());
  const [batteryToDelete, setBatteryToDelete] = useState<Battery>();
  const [editing, setEditing] = useState<number | undefined>();
  const [searchText, setSearchText] = useState<string>("");

  const [validated, setValidated] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  useEffect(() => {
    initBatteries();
    initTenants();
    // eslint-disable-next-line
  }, []);

  const openCreateModal = () => {
    setEditing(undefined);
    setShowEditModal(true);
    setNewBattery({} as Battery)
  }

  const handleDeleteModalClose = () => setShowDeleteModal(false);

  const handleCreateModalClose = () => {
    setShowEditModal(false);
    setValidated(false);
    setNewBattery(new Battery());
  }

  const openDeleteModal = (battery: Battery) => () => {
    setShowDeleteModal(true);
    setBatteryToDelete(battery);
  }

  const reset = () => {
    setBatteries(null);
    initBatteries();
  }

  const openUploadModal = () => setShowUploadModal(true);

  const handleUploadModalClose = () => setShowUploadModal(false);

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader!();

      const promise = editing !== undefined
        ? Batteries.updateBattery(newBattery)
        : Batteries.createBattery(newBattery);

      promise
        .then(
          () => {
            hideLoader!();
            showToast!('battery-success', `Battery ${editing !== undefined ? 'Updated' : 'Created'} successfully`, 'Success', 'success');
            reset();
            setNewBattery(new Battery());
            handleCreateModalClose();
            if (editing !== undefined) setEditing(undefined);
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            hideLoader!();
            showToast!('battery-error', message, `Batteries Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }

  }

  const handleUploadSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader!();

      let batteryUploadDTO: BatteryUploadDTO = {
        targets: [...organizations!.filter(o => o.checked).map(o => o.tenantId)],
        items: [{ type: 'batteries', ids: [...batteries!?.filter(p => p.checked).map(p => String(p.id))] }]
      }
      Batteries.uploadBatteries(batteryUploadDTO)
        .then(
          () => {
            hideLoader!();
            showToast!('battery-success', `Battery ${editing !== undefined ? 'Updated' : 'Created'} successfully`, 'Success', 'success');
            handleUploadModalClose();
          },
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            hideLoader!();
            showToast!('battery-error', message, `Batteries Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )

    }
  }

  const handleFieldChange = (field: keyof Battery) => (evt: React.ChangeEvent<any>) => {
    const value = evt.target.value;
    setNewBattery({
      ...newBattery,
      [field]: value,
    })
  }

  const handleCheckedBattery = (batteryIndex: number) => (evt: React.ChangeEvent<any>) => {

    const value = evt.target.checked;

    let updatedBatteries = [...batteries!]
    updatedBatteries[batteryIndex].checked = value;

    setBatteries([...updatedBatteries])

  }

  const handleChekedOrganization = (organizationIndex: number) => (evt: React.ChangeEvent<any>) => {

    const value = evt.target.checked;

    let updatedOrganizations = [...organizations!]
    updatedOrganizations[organizationIndex].checked = value;

    setOrganizations([...updatedOrganizations])

  }

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  }

  const deleteBattery = () => {
    showLoader!();

    Batteries.deleteBattery(batteryToDelete!.id!)
      .then(
        () => {
          hideLoader!();
          showToast!('battery-success', 'Battery deleted successfully', 'Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('battery-error', message, `Batteries Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const editBattery = (index: number) => () => {
    setShowEditModal(true);
    setEditing(index);
    setNewBattery({ ...batteries![index] });
  }

  const managebatteriesTasks = (id: string) => () => navigate(`/batteries/${id}/manage-tasks`);

  const initBatteries = () => {
    Batteries.getBatteries()
      .then(
        ({ data: batteries }) => {
          batteries.map((p) => { p.checked = false; return p });
          setBatteries(batteries)
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('batteries-error', message, `Batteries Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const initTenants = () => {
    Organizations.getOrganizations()
      .then(
        ({ data: organizations }) => { organizations.map((o) => { o.checked = false; return o }); setOrganizations(organizations) },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('organizations-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderBatteries = () => {
    const filteredBatteries = batteries!.filter(battery => battery.name.toLowerCase().includes(searchText.toLowerCase()));

    return (
      <>
        <Row className="gx-2 mb-3">
          <Col>
            <AppSearch value={searchText} onChange={handleSearchChange} placeholder={t('batteriesList.search')} />
          </Col>
        </Row>

        {filteredBatteries.length === 0 && (
          <Alert className="mb-0 py-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">{t('batteriesList.noBatteries')}</span>
          </Alert>
        )}

        {batteries!.filter(p => p.checked).length > 0 &&
          <Alert className="mb-2 py-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">{t('batteriesList.selectedBatteries')} {batteries!.filter(p => p.checked).length}</span>
          </Alert>
        }

        <ListGroup>
          {filteredBatteries.map((battery, index) => (
            <ListGroup.Item key={battery.id} className="d-flex align-items-center">
              <div className="pe-3">
                <Form.Check
                  inline
                  className='ms-3'
                  id={String(battery.id)}
                  type="checkbox"
                  checked={battery.checked}
                  onChange={handleCheckedBattery(index)}
                />
              </div>

              <Col className="overflow-hidden">
                <p className="mb-0 highlight h6" onClick={() => navigate(`/batteries/${battery.id}/details`)}> {battery.name}</p>
                <p className="text-truncate mb-0">{battery.code}</p>
                <p className="text-truncate mb-0">{battery.description}</p>
              </Col>

              <div className="ps-3 text-end">
                <IconButton className="ms-2" onClick={managebatteriesTasks(battery.id!)}>
                  <FontAwesomeIcon icon={solid('calendar-alt')} />
                </IconButton>
                <IconButton className="ms-2" onClick={editBattery(index)}>
                  <FontAwesomeIcon icon={solid('pen')} />
                </IconButton>
                <IconButton className="ms-2" onClick={openDeleteModal(battery)}>
                  <FontAwesomeIcon className="text-danger" icon={solid('trash')} />
                </IconButton>

              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </>
    )
  }

  const renderAsyncContent = () => {
    return batteries
      ? renderBatteries()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={150} />
        </div>
      )
  }

  return (
    <Container>
      <Card>
        <div className="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
          <Card.Title className="mb-0">{t('batteriesList.title')}</Card.Title>

          <div>
            <Button variant="light" size="sm" onClick={openUploadModal} disabled={!batteries ? true : batteries!.filter((p) => p.checked).length > 0 ? false : true}>
              <FontAwesomeIcon icon={solid('arrow-up-from-bracket')} />
              <span className="ms-2">{t('batteriesList.upload')}</span>
            </Button>

            <Button className="ms-3" variant="success" size="sm" onClick={openCreateModal}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">{t('batteriesList.create')}</span>
            </Button></div>
        </div>

        <Card.Body>{renderAsyncContent()}</Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.deleteTitle', { item: t('common.battery') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('batteriesList.deleteMessage')} <b>{batteryToDelete?.name}</b>?

          <h5 className="my-4 text-center text-danger">{t('common.deleteWarning')}</h5>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handleDeleteModalClose}>{t('common.cancel')}</Button>
          <Button variant="danger" size="sm" onClick={deleteBattery}>{t('common.delete')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleCreateModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editing !== undefined ? t('common.edit') : t('common.create')} {t('common.battery')}</Modal.Title>
        </Modal.Header>

        <Form validated={validated} noValidate onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group controlId="battery-name" className="mb-3">
              <Form.Label>{t('batteries.name')}</Form.Label>
              <Form.Control placeholder={t('batteryEdit.batteryName')} size="sm" value={newBattery.name} onChange={handleFieldChange('name')} required />
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('batteryEdit.batteryName') })}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="battery-description" className="mb-3">
              <Form.Label>{t('batteries.description')}</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder={t('batteryEdit.batteryDescription')} size="sm" value={newBattery.description} onChange={handleFieldChange('description')} />
            </Form.Group>

            <Form.Group controlId="battery-icon" className="mb-3">
              <Form.Label>{t('batteries.icon')}</Form.Label>
              <Form.Control as="input" placeholder={t('batteryEdit.batteryIcon')} size="sm" value={newBattery.icon} onChange={handleFieldChange('icon')} />
            </Form.Group>

            <Form.Group controlId="battery-target" className="mb-3">
              <Form.Label>{t('batteryEdit.batteryTarget')}</Form.Label>
              <Form.Select size="sm" value={newBattery.target} onChange={handleFieldChange('target')}>
                <option value="">{t('batteryEdit.selectTarget')}</option>
                <option key="A" value="A">{t('batteryEdit.adult')}</option>
                <option key="B" value="B">{t('batteryEdit.child')}</option>
                <option key="AB" value="AB">{t('batteryEdit.adultAndChild')}</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('batteryEdit.batteryTarget') })}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="battery-code" className="mb-3">
              <Form.Label>{t('batteries.code')}</Form.Label>
              <Form.Control as="input" placeholder={t('batteryEdit.batteryCode')} size="sm" value={newBattery.code} onChange={handleFieldChange('code')}>
              </Form.Control>
              <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.pathology') })}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handleCreateModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showUploadModal} onHide={handleUploadModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.upload')} {t('common.battery')}</Modal.Title>
        </Modal.Header>

        <Form validated={validated} noValidate onSubmit={handleUploadSubmit}>
          <Modal.Body>
            <ListGroup>
              {organizations ? organizations!.map((organizations, index) => (
                <ListGroup.Item key={organizations.id} className="d-flex align-items-center">
                  <div className="pe-3">
                    <FontAwesomeIcon icon={solid('sitemap')} size="lg" fixedWidth />
                  </div>

                  <Col className="overflow-hidden">
                    <h6 className="mb-0">{organizations.name}</h6>
                  </Col>

                  <div className="ps-3 text-end">
                    <Form.Check
                      inline
                      className='ms-3'
                      id={String(organizations.id)}
                      type="checkbox"
                      checked={organizations.checked}
                      onChange={handleChekedOrganization(index)}
                    />
                  </div>
                </ListGroup.Item>
              )) : <></>}
            </ListGroup>

          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handleUploadModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}
